// shared/estimate.ts
import type { EstimateItem, EstimateResponse } from "./types";
import { type Micros, mergeMicros, scaleMicros } from "./micros";

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Totals from a list of estimate items. `calories` = Σ kcal. Each macro/sugar is
 *  the sum only when EVERY item carries it, otherwise null — so a recompute never
 *  wipes existing totals when items lack per-item data. Micros are merged by key
 *  across whatever items provide them (null when none do). */
export function sumItems(items: EstimateItem[]): {
  calories: number; protein_g: number | null; carbs_g: number | null;
  fat_g: number | null; sugar_g: number | null; micros: Micros | null;
} {
  const calories = items.reduce((s, it) => s + (it.kcal || 0), 0);
  const macro = (key: "protein_g" | "carbs_g" | "fat_g" | "sugar_g"): number | null =>
    items.length > 0 && items.every((it) => typeof it[key] === "number")
      ? round1(items.reduce((s, it) => s + (it[key] as number), 0))
      : null;
  const merged = mergeMicros(items.map((it) => it.micros));
  return {
    calories,
    protein_g: macro("protein_g"),
    carbs_g: macro("carbs_g"),
    fat_g: macro("fat_g"),
    sugar_g: macro("sugar_g"),
    micros: Object.keys(merged).length ? merged : null,
  };
}

/** Rescale one estimate item to a new weight, scaling every present nutrient
 *  linearly (factor = newGrams / item.grams). Micros scale via scaleMicros.
 *  Returns the item unchanged when it has no positive grams to scale from, or
 *  when newGrams is not positive — there is no density to apply. */
export function scaleEstimateItem(item: EstimateItem, newGrams: number): EstimateItem {
  if (!item.grams || item.grams <= 0 || !(newGrams > 0)) return item;
  const f = newGrams / item.grams;
  const out: EstimateItem = { name: item.name, kcal: Math.round(item.kcal * f), grams: Math.round(newGrams) };
  if (item.unit !== undefined) out.unit = item.unit;
  for (const k of ["protein_g", "carbs_g", "fat_g", "sugar_g"] as const) {
    if (typeof item[k] === "number") out[k] = Math.round((item[k] as number) * f);
  }
  const scaled = scaleMicros(item.micros ?? null, f);
  if (scaled) out.micros = scaled;
  return out;
}

/** Pick the run whose TOTAL calories is the (lower-)median, keeping that run's
 *  item/gram breakdown coherent. Pure: sorts a copy, never mutates the input. */
export function medianSelect(responses: EstimateResponse[]): EstimateResponse {
  if (responses.length === 0) throw new Error("medianSelect requires at least one response");
  if (responses.length === 1) return responses[0];
  const sorted = [...responses].sort((a, b) => a.calories - b.calories);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

/** Names of items whose energy density (kcal per gram) is implausible — below
 *  ~0.2 (more watery than broth) or above ~9 (denser than pure fat). Items
 *  without a positive `grams` are skipped. For server-side logging only. */
export function implausibleItems(resp: EstimateResponse): string[] {
  return resp.items
    .filter((it) => typeof it.grams === "number" && it.grams > 0
      && (it.kcal / it.grams < 0.2 || it.kcal / it.grams > 9))
    .map((it) => `${it.name} (${it.kcal}kcal/${it.grams}g)`);
}
